// src/useDirectoryUsers.js - the people in the Microsoft tenant
import { useState, useCallback } from 'react';
import { supabase } from './supabaseClient';

/**
 * The tenant's people, for picking one instead of typing their address.
 *
 * Asks the server, not the browser. The first version of this asked MSAL for a
 * Microsoft token, and MSAL has no account — sign-in goes through
 * supabase.auth.signInWithOAuth, and Supabase's provider_token exists only on
 * the response to signing in. So it returned early, listed nobody, and showed
 * no reason why. That silence is fixed here too: `error` is always set when
 * nothing loads, and the field says what went wrong.
 *
 * /api/directory-users holds the app-only Microsoft credentials and checks the
 * caller is an admin with the user_management permission before answering.
 */
export default function useDirectoryUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (loading || loaded) return;
    setLoading(true);
    setError(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('Not signed in');

      const res = await fetch('/api/directory-users', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        // The function distinguishes a missing Graph permission from bad
        // credentials, and that distinction is worth putting on screen.
        // error first, hint second. The error names which variables are
        // missing or which AADSTS code came back; the hint is generic
        // advice. Showing only the hint — as this did — tells the reader
        // to go and set something without saying what.
        throw new Error(
          [body.error, body.hint].filter(Boolean).join(' ') || `Server returned ${res.status}`,
        );
      }

      setUsers(await res.json());
      setLoaded(true);
    } catch (err) {
      console.error('Could not list directory users:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [loading, loaded]);

  return { users, loading, error, load };
}
